import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, MailIcon, PhoneIcon, ArrowUpRightIcon, MessageSquareIcon } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
export function ContactWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const {
    t
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      setIsCompact(scrollPercent > 0.2);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <>
      <motion.button onClick={() => setIsOpen(true)} className="fixed right-5 md:right-8 bottom-8 z-[90] group flex items-center gap-3 rounded-full bg-surface/80 backdrop-blur-xl border border-white/10 shadow-2xl hover:bg-surface hover:border-white/20 transition-all duration-500" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      delay: 2,
      ease: [0.16, 1, 0.3, 1]
    }} whileHover={{
      scale: 1.02,
      y: -2
    }} whileTap={{
      scale: 0.98
    }} style={{
      padding: isCompact ? '12px' : '12px 20px',
      gap: isCompact ? '0px' : '12px',
      transition: 'padding 0.4s cubic-bezier(0.16, 1, 0.3, 1), gap 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
    }} data-id="element-104">
        <div className="absolute inset-0 rounded-full bg-primary/5 blur-md group-hover:bg-primary/10 transition-colors duration-500 -z-10" data-id="element-105" />

        <div className="relative flex items-center justify-center w-6 h-6 rounded-full bg-white/10 group-hover:bg-white/20 transition-colors duration-300" data-id="element-106">
          <MessageSquareIcon className="w-3 h-3 text-primary" data-id="element-107" />
          <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2" data-id="element-108">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-109" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" data-id="element-110" />
          </span>
        </div>

        <span className="text-xs tracking-[0.15em] uppercase font-medium text-primary overflow-hidden whitespace-nowrap" style={{
        maxWidth: isCompact ? '0px' : '120px',
        opacity: isCompact ? 0 : 1,
        paddingRight: isCompact ? '0px' : '4px',
        transition: 'max-width 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease, padding-right 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
      }} data-id="element-111">
          {t.widgetCTA}
        </span>
      </motion.button>

      {/* Panel */}
      <AnimatePresence data-id="element-112">
        {isOpen && <>
            <motion.div className="fixed inset-0 z-[95] bg-background/80 backdrop-blur-sm" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} onClick={() => setIsOpen(false)} data-id="element-113" />
            <motion.div className="fixed right-0 top-0 bottom-0 z-[100] w-full max-w-md bg-surface border-l border-border overflow-y-auto" initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-114">
              <div className="p-8 md:p-12 min-h-full flex flex-col" data-id="element-115">
                <div className="flex justify-end mb-12" data-id="element-116">
                  <button onClick={() => setIsOpen(false)} className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-secondary/40 transition-colors duration-300" data-id="element-117">
                    <XIcon className="w-4 h-4 text-secondary" data-id="element-118" />
                  </button>
                </div>
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2,
              ease: [0.16, 1, 0.3, 1]
            }} data-id="element-119">
                  <p className="text-secondary text-xs tracking-[0.2em] uppercase mb-4 font-light" data-id="element-120">
                    {t.contactOverline}
                  </p>
                  <h3 className="text-3xl md:text-4xl font-display font-bold text-primary tracking-tight mb-4 leading-snug" data-id="element-121">
                    {t.widgetTitle1}
                    <br data-id="element-122" />
                    {t.widgetTitle2}
                  </h3>
                  <motion.div className="w-12 h-px bg-gradient-to-r from-white/30 to-transparent mb-8" initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                duration: 0.8,
                delay: 0.4
              }} style={{
                transformOrigin: 'left'
              }} data-id="element-123" />
                  <p className="text-secondary text-base font-light leading-relaxed mb-12" data-id="element-124">
                    {t.widgetDesc}
                  </p>
                </motion.div>
                <div className="space-y-4 mb-12" data-id="element-125">
                  <motion.a href="mailto:zanikicu@gmail.com" initial={{
                opacity: 0,
                y: 15
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5,
                delay: 0.3,
                ease: [0.16, 1, 0.3, 1]
              }} className="group flex items-center gap-4 p-5 rounded-xl border border-border hover:border-secondary/30 hover:bg-white/[0.02] transition-all duration-500" data-id="element-126">
                    <div className="w-11 h-11 rounded-full border border-border flex items-center justify-center group-hover:border-secondary/40 transition-colors duration-500 shrink-0" data-id="element-127">
                      <MailIcon className="w-4 h-4 text-secondary group-hover:text-primary transition-colors duration-500" data-id="element-128" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-129">
                      <p className="text-[10px] text-secondary/40 tracking-[0.15em] uppercase font-light mb-0.5" data-id="element-130">
                        {t.contactEmail}
                      </p>
                      <span className="text-primary text-sm font-display font-semibold tracking-tight" data-id="element-131">
                        zanikicu@gmail.com
                      </span>
                    </div>
                    <ArrowUpRightIcon className="w-4 h-4 text-muted opacity-0 group-hover:opacity-100 transition-all duration-500 shrink-0" data-id="element-132" />
                  </motion.a>

                  <motion.a href="https://wa.me/385914305555" target="_blank" rel="noopener noreferrer" initial={{
                opacity: 0,
                y: 15
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5,
                delay: 0.4,
                ease: [0.16, 1, 0.3, 1]
              }} className="group flex items-center gap-4 p-5 rounded-xl border border-border hover:border-secondary/30 hover:bg-white/[0.02] transition-all duration-500" data-id="element-133">
                    <div className="w-11 h-11 rounded-full border border-border flex items-center justify-center group-hover:border-secondary/40 transition-colors duration-500 shrink-0" data-id="element-134">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 text-secondary group-hover:text-primary transition-colors duration-500" fill="currentColor" data-id="element-135">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-136" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-137">
                      <p className="text-[10px] text-secondary/40 tracking-[0.15em] uppercase font-light mb-0.5" data-id="element-138">
                        {t.contactWhatsApp}
                      </p>
                      <span className="text-primary text-sm font-display font-semibold tracking-tight" data-id="element-139">
                        {t.contactWhatsAppCTA}
                      </span>
                    </div>
                    <ArrowUpRightIcon className="w-4 h-4 text-muted opacity-0 group-hover:opacity-100 transition-all duration-500 shrink-0" data-id="element-140" />
                  </motion.a>
                </div>
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5,
              delay: 0.5
            }} className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-border" data-id="element-141">
                  <span className="relative flex h-2 w-2" data-id="element-142">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-143" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" data-id="element-144" />
                  </span>
                  <span className="text-xs text-secondary font-light tracking-wider" data-id="element-145">
                    {t.contactAvailable}
                  </span>
                </motion.div>
                <div className="flex-1" data-id="element-146" />
                <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5,
              delay: 0.6
            }} className="text-muted text-[10px] font-light tracking-wider mt-12 pt-6 border-t border-border" data-id="element-147">
                  {t.widgetBased}
                </motion.p>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}